import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { formatIDR, formatDateID } from "@/lib/format";
import { toast } from "sonner";
import { BadgeDollarSign, Sparkles, Plus, Search, Eye, Pencil, CheckCircle2, Trash2, X, Loader2, FileDown, Wallet, Printer } from "lucide-react";
import { MAROON, inpL } from "@/components/kiyana/LeaveForm";
import { usePager, Pager } from "@/components/kiyana/Pager";

const card = "bg-white rounded-2xl border border-slate-100";
const TABS = [
  { id: "belum_disetujui", label: "Belum Disetujui" },
  { id: "sudah_disetujui", label: "Sudah Disetujui" },
  { id: "sudah_dibayar", label: "Sudah Dibayar" },
];
const fmtDate = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T00:00:00+07:00`).toLocaleDateString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Asia/Jakarta" }) : "—");

export default function AdminProductionWages() {
  const [view, setView] = useState({ name: "list", editId: null });
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("belum_disetujui");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [genForm, setGenForm] = useState(() => {
    const today = new Date().toISOString().slice(0, 10);
    const weekAgo = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
    return { period: today.slice(0, 7), date: today, date_from: weekAgo, date_to: today, role: "" };
  });
  const [detail, setDetail] = useState(null);
  const [del, setDel] = useState(null);
  const [options, setOptions] = useState({ employees: [] });
  const [saldoMap, setSaldoMap] = useState({});
  const [genRows, setGenRows] = useState(null);
  const [candsLoading, setCandsLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkConfirm, setBulkConfirm] = useState(null); // { key: 'pay'|'approve'|'delete' }

  const load = useCallback(() => {
    const qs = tab ? `?status=${tab}` : "";
    api.get(`/prod/payouts${qs}`).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, [tab]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelected([]); }, [tab]);
  useEffect(() => {
    api.get("/prod/options").then((r) => setOptions({ employees: r.data.employees || [] })).catch(() => {});
    api.get("/prod/wages/saldo").then((r) => {
      const m = {};
      (r.data.items || []).forEach((s) => { m[s.employee_id] = s; });
      setSaldoMap(m);
    }).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    const list = items || [];
    return t ? list.filter((p) => `${p.number} ${p.employee_name}`.toLowerCase().includes(t)) : list;
  }, [items, q]);
  const { paged, pager } = usePager(filtered, 10);

  useEffect(() => {
    if (!showGenerate) { setGenRows(null); return; }
    if (!genForm.date_from || !genForm.date_to) return;
    setCandsLoading(true);
    api.get("/prod/payouts/generate-candidates", { params: { date_from: genForm.date_from, date_to: genForm.date_to, role: genForm.role } })
      .then((r) => setGenRows((r.data.items || []).map((c) => ({ ...c, nominal: String(Math.max(0, c.saldo)), checked: c.saldo > 10000 }))))
      .catch(() => { toast.error("Gagal memuat daftar saldo karyawan"); setGenRows([]); })
      .finally(() => setCandsLoading(false));
  }, [showGenerate, genForm.date_from, genForm.date_to, genForm.role]);

  const genTotal = useMemo(() => (genRows || []).filter((r) => r.checked && r.saldo > 10000)
    .reduce((a, r) => a + (parseInt(r.nominal, 10) || Math.max(0, r.saldo)), 0), [genRows]);

  const toggleAll = (e) => setGenRows((rows) => (rows || []).map((r) => r.saldo > 10000 ? { ...r, checked: e.target.checked } : r));

  const generate = async () => {
    if (!genForm.date_from || !genForm.date_to) return toast.error("Rentang tanggal kerja wajib diisi");
    const chosen = (genRows || []).filter((r) => r.checked && r.saldo > 10000);
    if (!chosen.length) return toast.error("Pilih minimal satu karyawan");
    setBusy(true);
    try {
      const items = chosen.map((r) => ({ employee_id: r.employee_id, nominal: parseInt(r.nominal, 10) || Math.max(0, r.saldo) }));
      const r = await api.post("/prod/payouts/generate", { ...genForm, items });
      toast.success(`${r.data.created} payout dibuat untuk ${genForm.date_from} s/d ${genForm.date_to}${r.data.skipped?.length ? ` • dilewati: ${r.data.skipped.length} karyawan` : ""}`);
      setShowGenerate(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal generate payout"); } finally { setBusy(false); }
  };

  const act = async (p, action) => {
    setBusy(true);
    try {
      await api.post(`/prod/payouts/${p.id}/${action}`);
      toast.success(action === "approve"
        ? `Payout ${p.number} disetujui — saldo ${p.employee_name} ${formatIDR(p.nominal)} dicairkan (slip PDF tersedia di portal karyawan)`
        : `Payout ${p.number} ditandai Sudah Dibayar`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memproses payout"); } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/prod/payouts/${del.id}`);
      toast.success(`Payout ${del.number} dihapus`);
      setDel(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); } finally { setBusy(false); }
  };

  const fetchSlipBlob = async (p) => {
    const r = await api.get("/prod/wages/slip.pdf", { params: { employee_id: p.employee_id, payout_id: p.id }, responseType: "blob" });
    return new Blob([r.data], { type: "application/pdf" });
  };

  const openPdfPrint = (blob, fname) => {
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    if (!w) { // pop-up diblokir → fallback unduh
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      toast.info("Pop-up diblokir — slip diunduh, buka file untuk mencetak");
      return;
    }
    w.onload = () => { try { w.focus(); w.print(); } catch {} };
  };

  const bulkSlips = async () => {
    const targets = filtered.filter((p) => selected.includes(p.id) && p.status !== "belum_disetujui");
    if (!targets.length) return toast.error("Slip gaji hanya tersedia untuk payout yang sudah disetujui");
    setBusy(true);
    try {
      const r = await api.post("/prod/payouts/slip-bulk", { payout_ids: targets.map((p) => p.id) }, { responseType: "blob" });
      openPdfPrint(new Blob([r.data], { type: "application/pdf" }), `slip-gaji-gabungan-${targets.length}.pdf`);
      toast.success(`${targets.length} slip gaji digabung dalam 1 PDF A4 — siap dicetak sekaligus`);
    } catch { toast.error("Tidak ada slip yang bisa dibuat — setujui payout dulu"); } finally { setBusy(false); }
  };

  const runBulkConfirm = async () => {
    const k = bulkConfirm?.key;
    setBulkConfirm(null);
    if (k === "pay") await doBulkAction("pay", "Sudah Dibayar");
    else if (k === "approve") await doBulkAction("approve", "Disetujui");
    else if (k === "delete") await doBulkDelete();
  };

  const doBulkAction = async (action, label) => {
    setBusy(true);
    let ok = 0, fail = 0;
    for (const id of selected) {
      try { await api.post(`/prod/payouts/${id}/${action}`); ok++; } catch { fail++; }
    }
    toast.success(`${ok} payout ditandai ${label}${fail ? ` • ${fail} gagal/dilewati` : ""}`);
    setSelected([]);
    setBusy(false);
    load();
  };

  const doBulkDelete = async () => {
    setBusy(true);
    let ok = 0, fail = 0;
    for (const id of selected) {
      try { await api.delete(`/prod/payouts/${id}`); ok++; } catch { fail++; }
    }
    toast.success(`${ok} payout dihapus${fail ? ` • ${fail} gagal (sudah diproses)` : ""}`);
    setSelected([]);
    setBusy(false);
    load();
  };

  const openDetail = async (p) => {
    try { const r = await api.get(`/prod/payouts/${p.id}`); setDetail(r.data); }
    catch { toast.error("Gagal memuat detail payout"); }
  };

  const downloadSlip = async (p) => {
    try {
      const r = await api.get("/prod/wages/slip.pdf", { params: { employee_id: p.employee_id, payout_id: p.id }, responseType: "blob" });
      openPdfPrint(new Blob([r.data], { type: "application/pdf" }), `slip-gaji-${(p.employee_name || "karyawan").replace(/\s+/g, "-")}.pdf`);
    } catch { toast.error("Slip belum tersedia — setujui payout dulu"); }
  };

  if (view.name === "create" || view.name === "edit") {
    return <PayoutForm editId={view.editId} employees={options.employees} saldoMap={saldoMap}
      onBack={() => { setView({ name: "list", editId: null }); load(); }} />;
  }

  return (
    <div data-testid="payout-page" className="space-y-5 pb-10">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] text-slate-400">Payout <span className="mx-1">›</span> Daftar</div>
          <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2"><BadgeDollarSign className="w-6 h-6 text-[#7F1D2D]" /> Payout</h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="payout-generate-btn" onClick={() => setShowGenerate(true)}
            className="h-10 px-4 rounded-xl border border-[#7F1D2D]/40 text-[#7F1D2D] text-xs font-semibold flex items-center gap-1.5">
            <Sparkles className="w-4 h-4" /> Generate Payout
          </button>
          <button data-testid="payout-create-btn" onClick={() => setView({ name: "create", editId: null })}
            className="h-10 px-4 rounded-xl text-white text-xs font-semibold flex items-center gap-1.5" style={{ background: MAROON }}>
            <Plus className="w-4 h-4" /> Buat Payout
          </button>
        </div>
      </div>

      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit" data-testid="payout-tabs">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`payout-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`h-9 px-4 rounded-lg text-xs font-semibold transition-colors ${tab === t.id ? "bg-white text-[#7F1D2D] shadow-sm" : "text-slate-500"}`}>{t.label}</button>
        ))}
      </div>

      <div className={`${card} overflow-hidden`}>
        <div className="px-5 py-3 border-b border-slate-50 flex items-center justify-between gap-3 flex-wrap">
          {selected.length > 0 ? (
            <div data-testid="payout-bulkbar" className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-slate-700">{selected.length} payout dipilih</span>
              {tab === "belum_disetujui" && (
                <button data-testid="payout-bulk-approve" onClick={() => setBulkConfirm({ key: "approve" })} disabled={busy}
                  className="h-7 px-3 rounded-lg bg-emerald-600 text-white text-[11px] font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-40">Approve Semua</button>
              )}
              {tab === "sudah_disetujui" && (
                <button data-testid="payout-bulk-pay" onClick={() => setBulkConfirm({ key: "pay" })} disabled={busy}
                  className="h-7 px-3 rounded-lg text-white text-[11px] font-semibold disabled:opacity-40" style={{ background: MAROON }}>Bayar Semua</button>
              )}
              {tab === "belum_disetujui" && (
                <button data-testid="payout-bulk-delete" onClick={() => setBulkConfirm({ key: "delete" })} disabled={busy}
                  className="h-7 px-3 rounded-lg border border-red-200 text-red-500 text-[11px] font-semibold hover:bg-red-50 transition-colors disabled:opacity-40">Hapus Semua</button>
              )}
              {tab !== "belum_disetujui" && (
                <button data-testid="payout-bulk-slip" onClick={bulkSlips} disabled={busy}
                  className="h-7 px-3 rounded-lg border border-slate-200 text-slate-600 text-[11px] font-semibold hover:border-[#7F1D2D] hover:text-[#7F1D2D] transition-colors disabled:opacity-40 flex items-center gap-1">
                  <Printer className="w-3 h-3" /> Print Slip Semua
                </button>
              )}
              <button onClick={() => setSelected([])} className="text-slate-400 hover:text-slate-600 text-[11px]">Batalkan pilihan</button>
            </div>
          ) : <span className="text-[11px] text-slate-400">Centang payout untuk aksi massal</span>}
          <div className="relative w-64">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="payout-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari" className={`${inpL} pl-9 h-9`} />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase text-slate-400 border-b border-slate-100 bg-slate-50/60">
                <th className="pl-5 pr-1 py-2.5 w-9">
                  <input type="checkbox" data-testid="payout-check-all" className="w-4 h-4 accent-[#7F1D2D]"
                    checked={paged.length > 0 && paged.every((p) => selected.includes(p.id))}
                    onChange={(e) => setSelected(e.target.checked ? paged.map((p) => p.id) : [])} />
                </th>
                <th className="px-3 py-2.5">Nomor Payout</th>
                <th className="px-3 py-2.5">Nama Karyawan</th>
                <th className="px-3 py-2.5">Tanggal</th>
                <th className="px-3 py-2.5 text-right">Nominal</th>
                <th className="px-3 py-2.5 text-right">Tunjangan</th>
                <th className="px-3 py-2.5 text-right">Potongan</th>
                <th className="px-3 py-2.5">Dibayar pada</th>
                <th className="px-3 py-2.5">Dibuat pada</th>
                <th className="px-5 py-2.5 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((p) => (
                <tr key={p.id} data-testid={`payout-row-${p.id}`} className={`border-b border-slate-50 ${selected.includes(p.id) ? "bg-rose-50/30" : ""}`}>
                  <td className="pl-5 pr-1 py-3">
                    <input type="checkbox" data-testid={`payout-check-${p.id}`} className="w-4 h-4 accent-[#7F1D2D]"
                      checked={selected.includes(p.id)}
                      onChange={(e) => setSelected((s) => e.target.checked ? [...s, p.id] : s.filter((x) => x !== p.id))} />
                  </td>
                  <td className="px-3 py-3 font-mono font-semibold text-slate-700">{p.number}</td>
                  <td className="px-3 py-3 font-semibold text-slate-700">{p.employee_name}</td>
                  <td className="px-3 py-3 text-slate-500">{fmtDate(p.date)}</td>
                  <td className="px-3 py-3 text-right font-bold text-slate-800">{formatIDR(p.nominal)}</td>
                  <td className="px-3 py-3 text-right text-emerald-600">{p.total_tunjangan ? formatIDR(p.total_tunjangan) : "Rp 0"}</td>
                  <td className="px-3 py-3 text-right text-red-500">{p.total_potongan ? formatIDR(p.total_potongan) : "Rp 0"}</td>
                  <td className="px-3 py-3 text-slate-500">{p.status === "sudah_dibayar" ? fmtDate(p.paid_at) : "Belum dibayar"}</td>
                  <td className="px-3 py-3 text-slate-500">{fmtDate(p.created_at)}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`payout-view-${p.id}`} onClick={() => openDetail(p)} title="Lihat"
                        className="h-8 w-8 rounded-lg border border-slate-200 text-slate-500 flex items-center justify-center"><Eye className="w-3.5 h-3.5" /></button>
                      {p.status === "belum_disetujui" && (
                        <>
                          <button data-testid={`payout-edit-${p.id}`} onClick={() => setView({ name: "edit", editId: p.id })} title="Ubah"
                            className="h-8 w-8 rounded-lg border border-slate-200 text-slate-500 flex items-center justify-center"><Pencil className="w-3.5 h-3.5" /></button>
                          <button data-testid={`payout-approve-${p.id}`} onClick={() => act(p, "approve")} disabled={busy} title="Approve — cairkan saldo karyawan"
                            className="h-8 px-2.5 rounded-lg text-white text-[11px] font-semibold flex items-center gap-1 disabled:opacity-40" style={{ background: "#059669" }}>
                            <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                          </button>
                          <button data-testid={`payout-delete-${p.id}`} onClick={() => setDel(p)} title="Hapus"
                            className="h-8 w-8 rounded-lg border border-red-200 text-red-500 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                        </>
                      )}
                      {p.status === "sudah_disetujui" && (
                        <>
                          <button data-testid={`payout-slip-${p.id}`} onClick={() => downloadSlip(p)} title="Unduh Slip PDF"
                            className="h-8 w-8 rounded-lg border border-[#7F1D2D]/40 text-[#7F1D2D] flex items-center justify-center"><FileDown className="w-3.5 h-3.5" /></button>
                          <button data-testid={`payout-pay-${p.id}`} onClick={() => act(p, "pay")} disabled={busy} title="Tandai Sudah Dibayar"
                            className="h-8 px-2.5 rounded-lg text-white text-[11px] font-semibold flex items-center gap-1 disabled:opacity-40" style={{ background: MAROON }}>
                            <Wallet className="w-3.5 h-3.5" /> Bayar
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {items && !paged.length && (
                <tr><td colSpan={10} className="py-10 text-center text-slate-400" data-testid="payout-empty">Belum ada payout pada tab ini.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-5 pb-3"><Pager {...pager} testid="payout-pager" /></div>
      </div>

      {showGenerate && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !busy && setShowGenerate(false)} data-testid="generate-overlay">
          <div className="bg-white rounded-2xl w-full max-w-3xl p-6 max-h-[92vh] overflow-y-auto text-left" onClick={(e) => e.stopPropagation()} data-testid="generate-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-display font-bold text-slate-900 flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#7F1D2D]" /> Generate Payout</h3>
                <p className="text-[11px] text-slate-400 mt-0.5">Daftar seluruh karyawan bersaldo aktif pada rentang tanggal kerja. Centang karyawan yang ingin dibuatkan payout, atur nominal per karyawan (Cairkan Seluruh atau sebagian).</p>
              </div>
              <button data-testid="generate-close" onClick={() => !busy && setShowGenerate(false)} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 shrink-0"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid sm:grid-cols-5 gap-3 mb-4">
              <div>
                <label className="text-[11px] font-semibold text-slate-500">Dari Tanggal Kerja <span className="text-red-500">*</span></label>
                <input data-testid="generate-date-from" type="date" value={genForm.date_from} onChange={(e) => setGenForm({ ...genForm, date_from: e.target.value })} className={inpL} />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500">Sampai Tanggal Kerja <span className="text-red-500">*</span></label>
                <input data-testid="generate-date-to" type="date" value={genForm.date_to} onChange={(e) => setGenForm({ ...genForm, date_to: e.target.value })} className={inpL} />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500">Role Karyawan</label>
                <select data-testid="generate-role" value={genForm.role} onChange={(e) => setGenForm({ ...genForm, role: e.target.value })} className={inpL}>
                  <option value="">Semua Role</option>
                  <option value="penjahit">Penjahit</option>
                  <option value="cutting">Cutting</option>
                  <option value="finishing_qc">Finishing / QC</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500">Periode Payroll</label>
                <input data-testid="generate-period" type="month" value={genForm.period} onChange={(e) => setGenForm({ ...genForm, period: e.target.value })} className={inpL} />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500">Tanggal Payout</label>
                <input data-testid="generate-date" type="date" value={genForm.date} onChange={(e) => setGenForm({ ...genForm, date: e.target.value })} className={inpL} />
              </div>
            </div>

            {candsLoading ? (
              <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#7F1D2D]" /></div>
            ) : (
              <div className="border border-slate-100 rounded-xl overflow-hidden mb-4" data-testid="generate-candidates">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase text-slate-400 border-b border-slate-100 bg-slate-50/60">
                      <th className="px-4 py-2.5 w-9">
                        <input type="checkbox" data-testid="gen-check-all" checked={!!(genRows || []).length && (genRows || []).every((r) => r.saldo > 10000 ? r.checked : true)} onChange={toggleAll} className="w-4 h-4 accent-[#7F1D2D]" />
                      </th>
                      <th className="px-3 py-2.5">Karyawan</th>
                      <th className="px-3 py-2.5">Role</th>
                      <th className="px-3 py-2.5 text-right">Saldo Aktif</th>
                      <th className="px-3 py-2.5 text-right">Upah Periode</th>
                      <th className="px-3 py-2.5 text-right">Kasbon</th>
                      <th className="px-3 py-2.5 text-right">Dicairkan</th>
                      <th className="px-3 py-2.5 text-right">Nominal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(genRows || []).map((r) => (
                      <tr key={r.employee_id} data-testid={`gen-row-${r.employee_id}`} className={`border-b border-slate-50 ${r.saldo <= 10000 ? "opacity-50" : ""}`}>
                        <td className="px-4 py-2.5">
                          <input type="checkbox" data-testid={`gen-check-${r.employee_id}`} disabled={r.saldo <= 10000} checked={!!r.checked}
                            onChange={(e) => setGenRows((rows) => rows.map((x) => x.employee_id === r.employee_id ? { ...x, checked: e.target.checked } : x))}
                            className="w-4 h-4 accent-[#7F1D2D]" />
                        </td>
                        <td className="px-3 py-2.5 font-semibold text-slate-700">{r.employee_name || "—"}</td>
                        <td className="px-3 py-2.5 text-slate-500">{r.role_label || "—"}</td>
                        <td className={`px-3 py-2.5 text-right font-bold ${r.total_open - r.kasbon_sisa < 0 ? "text-red-500" : "text-emerald-600"}`}>{formatIDR(r.total_open - r.kasbon_sisa)}</td>
                        <td className="px-3 py-2.5 text-right text-slate-500">{formatIDR(r.upah)}</td>
                        <td className="px-3 py-2.5 text-right text-amber-600">{r.kasbon_sisa ? `-${formatIDR(r.kasbon_sisa)}` : "—"}</td>
                        <td className="px-3 py-2.5 text-right font-bold text-slate-800">{formatIDR(r.saldo)}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center justify-end gap-1.5">
                            <input data-testid={`gen-nominal-${r.employee_id}`} inputMode="numeric" disabled={r.saldo <= 10000}
                              value={r.nominal} onChange={(e) => setGenRows((rows) => rows.map((x) => x.employee_id === r.employee_id ? { ...x, nominal: e.target.value.replace(/\D/g, "") } : x))}
                              className="h-8 w-28 rounded-lg border border-slate-200 px-2.5 text-right text-xs font-semibold" placeholder="0" />
                            <button data-testid={`gen-full-${r.employee_id}`} disabled={r.saldo <= 10000}
                              onClick={() => setGenRows((rows) => rows.map((x) => x.employee_id === r.employee_id ? { ...x, nominal: String(Math.max(0, r.saldo)) } : x))}
                              className="h-8 px-2 rounded-lg border border-[#7F1D2D]/40 text-[#7F1D2D] text-[10px] font-semibold disabled:opacity-40">Seluruh</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!(genRows || []).length && (
                      <tr><td colSpan={8} className="py-8 text-center text-slate-400" data-testid="gen-empty">Tidak ada karyawan bersaldo aktif pada rentang tanggal kerja ini.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[11px] text-slate-500" data-testid="gen-summary">
                <b>{(genRows || []).filter((r) => r.checked && r.saldo > 10000).length}</b> karyawan terpilih • total pencairan <b className="text-[#7F1D2D]">{formatIDR(genTotal)}</b>
                <div className="text-[10px] text-slate-400">Nominal kosong/diisi 0 = cairkan seluruh saldo periode. Karyawan ber-saldo ≤ Rp 10.000 tidak ikut.</div>
              </div>
              <div className="flex gap-2">
                <button data-testid="generate-cancel" onClick={() => setShowGenerate(false)} disabled={busy}
                  className="h-10 px-5 rounded-xl border border-slate-200 text-slate-500 text-xs font-semibold disabled:opacity-40">Batal</button>
                <button data-testid="generate-submit" onClick={generate} disabled={busy || !(genRows || []).some((r) => r.checked && r.saldo > 10000)}
                  className="h-10 px-6 rounded-xl text-white text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-40" style={{ background: MAROON }}>
                  {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Generate {(genRows || []).filter((r) => r.checked && r.saldo > 10000).length} Payout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {del && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !busy && setDel(null)} data-testid="delete-overlay">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 text-center" onClick={(e) => e.stopPropagation()} data-testid="delete-modal">
            <div className="w-12 h-12 rounded-full bg-red-50 text-red-500 flex items-center justify-center mx-auto mb-3"><Trash2 className="w-5 h-5" /></div>
            <h3 className="font-display font-bold text-slate-900">Hapus Payout {del.number}?</h3>
            <p className="text-xs text-slate-500 mt-1.5 mb-4">Payout untuk {del.employee_name} ({formatIDR(del.netto)}) akan dihapus permanen. Saldo karyawan tidak berubah.</p>
            <div className="flex gap-2">
              <button data-testid="delete-cancel" onClick={() => setDel(null)} disabled={busy}
                className="flex-1 h-10 rounded-xl border border-slate-200 text-slate-500 text-xs font-semibold disabled:opacity-40">Batal</button>
              <button data-testid="delete-submit" onClick={remove} disabled={busy}
                className="flex-1 h-10 rounded-xl bg-red-600 text-white text-xs font-semibold disabled:opacity-40">Hapus</button>
            </div>
          </div>
        </div>
      )}

      {bulkConfirm && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !busy && setBulkConfirm(null)} data-testid="bulk-overlay">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()} data-testid="bulk-modal">
            <h3 className="font-display font-bold text-slate-900 mb-1.5">
              {bulkConfirm.key === "pay" ? "Bayar Semua Payout Terpilih" : bulkConfirm.key === "approve" ? "Approve Semua Payout Terpilih" : "Hapus Semua Payout Terpilih"}
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              {bulkConfirm.key === "pay" && <>Tandai <b>{selected.length} payout</b> sebagai Sudah Dibayar? Uang dianggap sudah diserahkan ke karyawan dan upahnya ditandai terbayar.</>}
              {bulkConfirm.key === "approve" && <>Approve <b>{selected.length} payout</b> sekaligus? Saldo masing-masing karyawan akan dicairkan sesuai nominal payout-nya dan slip PDF menjadi tersedia.</>}
              {bulkConfirm.key === "delete" && <>Hapus <b>{selected.length} payout</b> terpilih secara permanen? Hanya yang berstatus Belum Disetujui yang terhapus; saldo karyawan tidak berubah.</>}
            </p>
            <div className="flex gap-2">
              <button data-testid="bulk-cancel" onClick={() => setBulkConfirm(null)} disabled={busy}
                className="flex-1 h-10 rounded-xl border border-slate-200 text-slate-500 text-xs font-semibold disabled:opacity-40">Batal</button>
              <button data-testid="bulk-submit" onClick={runBulkConfirm} disabled={busy}
                className={`flex-1 h-10 rounded-xl text-white text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-40 ${bulkConfirm.key === "delete" ? "bg-red-600" : bulkConfirm.key === "approve" ? "bg-emerald-600" : ""}`}
                style={bulkConfirm.key === "pay" ? { background: MAROON } : {}}>
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Ya, Lanjutkan
              </button>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDetail(null)} data-testid="detail-overlay">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="detail-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-display font-bold text-slate-900">Payout {detail.number}</h3>
                <p className="text-[11px] text-slate-400">{TABS.find((t) => t.id === detail.status)?.label} • dibuat {fmtDate(detail.created_at)} oleh {detail.created_by}</p>
              </div>
              <button data-testid="detail-close" onClick={() => setDetail(null)} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500"><X className="w-4 h-4" /></button>
            </div>
            <div className={`${card} p-4 mb-3`}>
              <div className="text-[11px] font-bold text-slate-800 mb-2">Detail Karyawan</div>
              <div className="grid grid-cols-3 gap-y-2 text-xs">
                <div><div className="text-slate-400 text-[10px]">Nama Lengkap</div><div className="font-semibold text-slate-700">{detail.employee_name}</div></div>
                <div><div className="text-slate-400 text-[10px]">NRP</div><div className="font-semibold text-slate-700">{detail.employee_nrp}</div></div>
                <div><div className="text-slate-400 text-[10px]">Tipe Karyawan</div><div className="font-semibold text-slate-700">{detail.employee_role}</div></div>
                <div><div className="text-slate-400 text-[10px]">No. Telepon</div><div className="font-semibold text-slate-700">{detail.employee_phone}</div></div>
                <div><div className="text-slate-400 text-[10px]">Tanggal</div><div className="font-semibold text-slate-700">{fmtDate(detail.date)}</div></div>
                <div><div className="text-slate-400 text-[10px]">Periode Payroll</div><div className="font-semibold text-slate-700">{detail.period}</div></div>
              </div>
            </div>
            <div className={`${card} p-4 mb-3`}>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between"><span className="text-slate-500">Nominal (saldo upah)</span><span className="font-semibold">{formatIDR(detail.nominal)}</span></div>
                {(detail.tunjangan || []).map((t) => (
                  <div key={t.id} className="flex justify-between"><span className="text-slate-500">+ {t.name}{t.desc ? ` — ${t.desc}` : ""}</span><span className="text-emerald-600 font-semibold">{formatIDR(t.amount)}</span></div>
                ))}
                {(detail.potongan || []).map((t) => (
                  <div key={t.id} className="flex justify-between"><span className="text-slate-500">− {t.name}{t.desc ? ` — ${t.desc}` : ""}</span><span className="text-red-500 font-semibold">-{formatIDR(t.amount)}</span></div>
                ))}
                <div className="flex justify-between pt-2 border-t border-slate-100 text-sm"><span className="font-bold text-slate-800">Netto</span><span className="font-bold text-[#7F1D2D]">{formatIDR(detail.netto)}</span></div>
              </div>
            </div>
            {(detail.work_items || []).length > 0 && (
              <div className={`${card} p-4`}>
                <div className="text-[11px] font-bold text-slate-800 mb-2">Riwayat Pekerjaan Dicairkan ({detail.work_items.length})</div>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {detail.work_items.map((w) => (
                    <div key={w.id} className="flex items-center justify-between text-[11px] border-b border-slate-50 last:border-0 pb-1.5">
                      <div>
                        <span className="font-semibold text-slate-700 capitalize">{w.stage === "manual" ? "Manual" : w.stage}</span>
                        <span className="text-slate-400"> • {w.ref_number || "—"}{w.qty ? ` • ${w.qty} pcs` : ""}</span>
                        <div className="text-[9px] text-slate-400">{w.work_date || (w.created_at || "").slice(0, 10)}</div>
                      </div>
                      <span className="font-bold text-slate-700">{formatIDR(w.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {detail.status !== "belum_disetujui" && (
              <button data-testid="detail-slip-btn" onClick={() => downloadSlip(detail)}
                className="w-full mt-3 h-10 rounded-xl border border-[#7F1D2D]/40 text-[#7F1D2D] text-xs font-semibold flex items-center justify-center gap-1.5">
                <FileDown className="w-4 h-4" /> Unduh Slip Gaji PDF
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PayoutForm({ editId, employees, saldoMap, onBack }) {
  const today = new Date().toISOString().slice(0, 10);
  const curPeriod = new Date().toISOString().slice(0, 7);
  const [form, setForm] = useState({ number: "", employee_id: "", date: today, period: curPeriod, nominal: "", date_from: "", date_to: "" });
  const [tunjangan, setTunjangan] = useState([]);
  const [potongan, setPotongan] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(!editId);
  const [suggested, setSuggested] = useState("");

  useEffect(() => {
    if (!editId) {
      api.post("/prod/payouts/preview-number", {}).then((r) => setForm((f) => ({ ...f, number: r.data.number }))).catch(() => {});
      return;
    }
    api.get(`/prod/payouts/${editId}`).then((r) => {
      const p = r.data;
      setForm({ number: p.number, employee_id: p.employee_id, date: p.date, period: p.period, nominal: String(p.nominal), date_from: p.date_from || "", date_to: p.date_to || "" });
      setTunjangan(p.tunjangan || []);
      setPotongan(p.potongan || []);
      setLoaded(true);
    }).catch(() => { toast.error("Gagal memuat payout"); onBack(); });
  }, [editId]);

  const emp = employees.find((e) => e.id === form.employee_id);
  const saldo = form.employee_id ? saldoMap[form.employee_id] : null;

  useEffect(() => {
    if (emp && saldo && !form.nominal) setSuggested(String(Math.max(0, saldo.saldo || 0)));
  }, [form.employee_id]);

  const addComp = (list, setter, name) => setter([...list, { id: `new-${Date.now()}`, name: "", amount: "", desc: "", _kind: name }]);
  const setComp = (list, setter, id, patch) => setter(list.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const delComp = (list, setter, id) => setter(list.filter((c) => c.id !== id));

  const submit = async (andNew = false) => {
    if (!form.employee_id) return toast.error("Pilih karyawan");
    const nominal = parseInt(String(form.nominal).replace(/\D/g, ""), 10);
    if (!(nominal > 0)) return toast.error("Nominal wajib diisi");
    if (!form.date || !form.period) return toast.error("Tanggal & periode wajib diisi");
    for (const c of [...tunjangan, ...potongan]) {
      if (!c.name.trim()) return toast.error("Nama tunjangan/potongan wajib diisi");
      if (!(parseInt(String(c.amount).replace(/\D/g, ""), 10) >= 0)) return toast.error("Nominal komponen tidak valid");
    }
    setBusy(true);
    try {
      const payload = {
        employee_id: form.employee_id, date: form.date, period: form.period, nominal,
        date_from: form.date_from, date_to: form.date_to,
        tunjangan: tunjangan.map((c) => ({ name: c.name.trim(), amount: parseInt(String(c.amount).replace(/\D/g, ""), 10) || 0, desc: c.desc || "" })),
        potongan: potongan.map((c) => ({ name: c.name.trim(), amount: parseInt(String(c.amount).replace(/\D/g, ""), 10) || 0, desc: c.desc || "" })),
      };
      if (editId) {
        await api.put(`/prod/payouts/${editId}`, payload);
        toast.success("Payout diperbarui");
      } else {
        await api.post("/prod/payouts", { ...payload, number: form.number || undefined });
        toast.success("Payout dibuat — status Belum Disetujui");
      }
      if (andNew && !editId) {
        setForm({ number: "", employee_id: "", date: today, period: curPeriod, nominal: "", date_from: "", date_to: "" });
        setTunjangan([]); setPotongan([]);
        api.post("/prod/payouts/preview-number", {}).then((r) => setForm((f) => ({ ...f, number: r.data.number }))).catch(() => {});
      } else {
        onBack();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan payout"); } finally { setBusy(false); }
  };

  if (!loaded) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#7F1D2D]" /></div>;

  const CompSection = ({ title, placeholder, list, setter, tid }) => (
    <div className={`${card} p-4`} data-testid={`${tid}-section`}>
      <div className="text-xs font-bold text-slate-800 mb-3">{title}</div>
      <div className="space-y-2">
        <div className="grid grid-cols-[1fr_140px_1fr_32px] gap-2 text-[10px] uppercase text-slate-400 px-1">
          <span>Nama {title} <span className="text-red-500">*</span></span><span>Nominal <span className="text-red-500">*</span></span><span>Deskripsi</span><span />
        </div>
        {list.map((c) => (
          <div key={c.id} className="grid grid-cols-[1fr_140px_1fr_32px] gap-2 items-center">
            <input data-testid={`${tid}-name-${c.id}`} value={c.name} onChange={(e) => setComp(list, setter, c.id, { name: e.target.value })} placeholder={placeholder} className={inpL} />
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[11px] text-slate-400">Rp</span>
              <input data-testid={`${tid}-amount-${c.id}`} inputMode="numeric" value={c.amount} onChange={(e) => setComp(list, setter, c.id, { amount: e.target.value.replace(/\D/g, "") })} className={`${inpL} pl-8`} />
            </div>
            <input data-testid={`${tid}-desc-${c.id}`} value={c.desc} onChange={(e) => setComp(list, setter, c.id, { desc: e.target.value })} className={inpL} />
            <button data-testid={`${tid}-del-${c.id}`} onClick={() => delComp(list, setter, c.id)} className="h-8 w-8 rounded-lg text-red-400 hover:bg-red-50 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
      <button data-testid={`${tid}-add`} onClick={() => addComp(list, setter, tid)} className="mt-3 mx-auto flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 border border-slate-200 rounded-lg h-8 px-3">
        <Plus className="w-3 h-3" /> Tambahkan ke {title.toLowerCase()}
      </button>
    </div>
  );

  return (
    <div data-testid="payout-form-page" className="space-y-5 pb-10">
      <div>
        <div className="text-[11px] text-slate-400">Payout <span className="mx-1">›</span> {editId ? "Ubah" : "Buat"}</div>
        <h1 className="font-display text-2xl font-bold text-slate-900">{editId ? "Ubah Payout" : "Buat Payout"}</h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 items-start">
        <div className={`${card} p-4 space-y-3.5`}>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Nomor Payout</label>
            <input data-testid="pf-number" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value.toUpperCase() })} className={`${inpL} font-mono`} placeholder="Otomatis" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Karyawan <span className="text-red-500">*</span></label>
            <select data-testid="pf-employee" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value, nominal: "" })} className={inpL}>
              <option value="">Pilih salah satu opsi</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{`${e.name} (${e.employee_id || e.role_label || e.role})`}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Tanggal <span className="text-red-500">*</span></label>
              <input data-testid="pf-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inpL} />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Periode Payroll <span className="text-red-500">*</span></label>
              <input data-testid="pf-period" type="month" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} className={inpL} />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Nominal <span className="text-red-500">*</span></label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">Rp</span>
              <input data-testid="pf-nominal" inputMode="numeric" value={form.nominal} onChange={(e) => setForm({ ...form, nominal: e.target.value.replace(/\D/g, "") })}
                placeholder={suggested || "0"} className={`${inpL} pl-9`} />
            </div>
            {saldo && !form.nominal && suggested && suggested !== "0" && (
              <button data-testid="pf-nominal-fill" onClick={() => setForm({ ...form, nominal: suggested })} className="text-[10px] text-[#7F1D2D] font-semibold mt-1">
                Isi sesuai saldo aktif: {formatIDR(parseInt(suggested))}
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Sortir dari Tanggal</label>
              <input data-testid="pf-date-from" type="date" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })} className={inpL} />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Sampai Tanggal</label>
              <input data-testid="pf-date-to" type="date" value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })} className={inpL} />
            </div>
          </div>
          <p className="text-[10px] text-slate-400">Sortir tanggal opsional — saat Approve, hanya upah pada rentang tanggal kerja ini yang dicairkan. Sisa upah lain tetap mengendap di saldo.</p>
        </div>

        <div className={`${card} p-4`} data-testid="pf-emp-detail">
          <div className="text-xs font-bold text-slate-800">Detail Karyawan</div>
          <p className="text-[10px] text-slate-400 mb-3">Informasi lengkap karyawan yang dipilih untuk payout</p>
          <div className="grid grid-cols-3 gap-y-3 text-xs">
            <div><div className="text-slate-400 text-[10px]">Nama Lengkap</div><div className="font-semibold text-slate-700">{emp?.name || "-"}</div></div>
            <div><div className="text-slate-400 text-[10px]">NRP</div><div className="font-semibold text-slate-700">{emp?.employee_id || "-"}</div></div>
            <div><div className="text-slate-400 text-[10px]">Saldo, Pinjaman & Denda</div><div className={`font-bold ${saldo?.saldo < 0 ? "text-red-500" : "text-emerald-600"}`}>{saldo ? formatIDR(saldo.saldo) : "-"}</div></div>
            <div><div className="text-slate-400 text-[10px]">No. Telepon</div><div className="font-semibold text-slate-700">{emp?.phone || "-"}</div></div>
            <div><div className="text-slate-400 text-[10px]">Tipe Karyawan</div><div className="font-semibold text-slate-700">{emp?.role_label || emp?.role || "-"}</div></div>
            <div><div className="text-slate-400 text-[10px]">Sisa Kasbon</div><div className="font-semibold text-amber-600">{saldo?.kasbon_sisa ? formatIDR(saldo.kasbon_sisa) : "-"}</div></div>
          </div>
        </div>
      </div>

      <CompSection title="Tunjangan" placeholder="Contoh: Tunjangan Makan" list={tunjangan} setter={setTunjangan} tid="pf-tunjangan" />
      <CompSection title="Potongan" placeholder="Contoh: Potongan Kasbon" list={potongan} setter={setPotongan} tid="pf-potongan" />

      <div className="flex gap-2">
        <button data-testid="pf-submit" onClick={() => submit(false)} disabled={busy}
          className="h-10 px-6 rounded-xl text-white text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40" style={{ background: MAROON }}>
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {editId ? "Simpan Perubahan" : "Buat"}
        </button>
        {!editId && (
          <button data-testid="pf-submit-new" onClick={() => submit(true)} disabled={busy}
            className="h-10 px-5 rounded-xl border border-slate-200 text-slate-600 text-xs font-semibold disabled:opacity-40">Buat & buat lainnya</button>
        )}
        <button data-testid="pf-cancel" onClick={onBack} disabled={busy}
          className="h-10 px-5 rounded-xl border border-slate-200 text-slate-500 text-xs font-semibold disabled:opacity-40">Batal</button>
      </div>
    </div>
  );
}
